import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import { MessageSquare, Phone, Shield, Users, Check, ArrowUpRight, ShieldCheck, MessageCircle, Bell, Globe, Zap, Users as UsersIcon, ChevronDown, ChevronUp, Star, Lock, Smartphone, Award, Search, Clock, FileText, TrendingUp, Target, BarChart, MapPin, Globe2, Building2, Store, ShoppingCart, HeadphonesIcon, Stethoscope, GraduationCap, Truck, Coffee, Sparkles, Clock1, Send, Users2, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: Smartphone, title: 'Global consumer reach', text: 'Connect with millions of mobile users worldwide. ZuriChat is our own mobile platform with deep integration. OTP delivery available globally.' },
  { icon: CheckCircle2, title: 'Permanent blue badge verification', text: 'One-time $99 verification fee for permanent business verification status. No annual subscription. Build instant trust with customers through verified business presence.' },
  { icon: ShieldCheck, title: 'Customer-initiated conversations only', text: 'Businesses respond only when customers contact them, preventing spam and ensuring customers control their messaging experience. Similar to WhatsApp Business policies.' },
  { icon: Lock, title: 'Enterprise-grade security', text: 'Complete workspace isolation ensures your business conversations are secure and compliant. Each workspace has isolated access controls and data protection.' },
  { icon: Globe2, title: 'Worldwide business numbers', text: 'Register business numbers from any country with global OTP delivery. Serve customers in your local market and internationally.' },
  { icon: Bell, title: 'Real-time push notifications', text: 'Never miss a customer inquiry with instant push notifications. Agents receive alerts immediately when customers message your business number.' },
];

const benefits = [
  { title: 'One-time verification fee', text: 'Pay $99 once for permanent blue badge verification. No annual renewal fees or hidden costs. Your business verification status lasts forever.' },
  { title: 'Unified inbox integration', text: 'All ZuriChat messages flow into your unified inbox alongside WhatsApp, email, and other channels. Manage all customer communications from one place.' },
  { title: 'Professional business profile', text: 'Customize your business with logo, business name, and full details. Your business name appears as the primary display name to customers.' },
  { title: 'Team collaboration tools', text: 'Multiple agents can handle ZuriChat conversations with assignment, internal notes, escalation, and real-time collaboration features.' },
];

const verificationSteps = [
  { step: '1', title: 'Register your business number', text: 'Add your business phone number to your workspace with complete profile information including business name, logo, and contact details.' },
  { step: '2', title: 'Verify with OTP', text: 'Receive a 6-digit OTP via SMS to confirm phone number ownership. The OTP is valid for 5 minutes and works just like mobile app verification.' },
  { step: '3', title: 'Get blue badge', text: 'Complete one-time $99 verification payment to receive permanent blue badge status. Your business is now verified and trusted by customers.' },
  { step: '4', title: 'Start receiving messages', text: 'Verified businesses can receive and respond to customer messages from the ZuriChat app. Messages appear in your unified inbox for easy management.' },
];

const industries = [
  { title: 'Retail & E-commerce', text: 'Handle order inquiries, shipping updates, returns, and product questions. Provide instant customer support for better conversion rates worldwide.' },
  { title: 'Professional Services', text: 'Lawyers, doctors, consultants, accountants, and freelancers offer consultations and appointment scheduling. Share documents and schedule meetings globally.' },
  { title: 'Healthcare & Medical', text: 'Provide appointment reminders, prescription information, and patient support. Share medical information securely and maintain compliance with local regulations.' },
  { title: 'Education & Training', text: 'Handle enrollment inquiries, course information, and student support. Share educational content and schedule virtual sessions for international students.' },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'What is ZuriChat Business?',
    answer: 'ZuriChat Business is our enterprise messaging platform that allows businesses to register their phone numbers and receive messages from ZuriChat app users. ZuriChat is our consumer mobile application with millions of users across Africa. Businesses can get verified with a blue badge and receive customer inquiries directly in their unified inbox.',
  },
  {
    question: 'How does business verification work?',
    answer: 'Business verification requires a one-time $99 payment. You register your business number with complete profile information (business name, logo, full name, username), verify ownership via OTP sent to your phone, and then complete verification. Once approved, you receive a permanent blue badge that appears next to your business name in the app. No annual renewal fees.',
  },
  {
    question: 'Can businesses initiate conversations with customers?',
    answer: 'No, businesses can only respond to customer-initiated conversations. This prevents spam and ensures customers control when they want to interact with businesses. This policy is similar to WhatsApp Business messaging rules and creates a better customer experience.',
  },
  {
    question: 'How is this different from WhatsApp Business?',
    answer: 'ZuriChat Business is our own platform with better integration into our business workspace system. Messages flow directly into your unified inbox, you get real-time notifications, and you can manage conversations alongside other channels. Plus, verification is permanent (one-time $99 fee) rather than annual subscription, and you have access to our AI-powered features like Zyra Agent.',
  },
  {
    question: 'What countries are supported for business numbers?',
    answer: 'ZuriChat business numbers are available worldwide. OTP delivery works globally, so you can register business numbers from any country. Serve customers globally with the same platform.',
  },
  {
    question: 'Can I have multiple business numbers for my business?',
    answer: 'Each workspace can have only one business number to maintain consistency and prevent customer confusion. If you need multiple business numbers for different business entities or locations, you can create separate workspaces for each one.',
  },
  {
    question: 'Is the blue badge verification permanent?',
    answer: 'Yes, the blue badge is permanent after the one-time $99 verification payment. Unlike other platforms that charge annual fees for verification, our verification is a one-time investment for permanent business verification status. Your business remains verified forever.',
  },
  {
    question: 'How do messages appear in my business workspace?',
    answer: 'ZuriChat messages automatically bridge to your unified inbox where you can manage them alongside messages from WhatsApp, email, and other channels. Agents can respond directly from the workspace, and responses are sent back to the customer\'s ZuriChat app in real-time. All message history is preserved.',
  },
  {
    question: 'What profile information is required for business verification?',
    answer: 'You need to provide your business name (primary display name), business logo (required), username (unique handle), and full name (for KYC verification). The business name is what customers see, while the full name is used for verification purposes only.',
  },
  {
    question: 'How long does OTP verification take?',
    answer: 'OTP verification is instant. When you register your business number, you receive a 6-digit OTP via SMS within seconds. The OTP is valid for 5 minutes, giving you plenty of time to complete verification. The system uses the same reliable SMS infrastructure as our mobile app.',
  },
  {
    question: 'Can I use my existing phone number for ZuriChat Business?',
    answer: 'You can use any phone number that is not already registered in the ZuriChat consumer app. If your number is already registered as a personal ZuriChat account, you cannot use it as a business number. This prevents conflicts and ensures clean separation between personal and business use.',
  },
];

export default function ChannelsZurichat() {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  return (
    <>
      <SEO
        title="ZuriChat Business Messaging - Connect with Global Customers | Blue Badge Verification"
        description="Register your business number on ZuriChat and receive messages from millions of mobile users worldwide. One-time $99 blue badge verification. Customer-initiated conversations, unified inbox, real-time notifications. Global OTP delivery available."
        path="/channels/zurichat"
        keywords="ZuriChat business, global messaging, business verification, blue badge, customer communication, mobile business messaging, WhatsApp alternative, worldwide business messaging, international customer chat, global business communication, enterprise messaging platform, OTP delivery worldwide"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Globe2 className="h-4 w-4" /> Available worldwide
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                Business messaging for <span className="gradient-text">global customers</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Register your business number on ZuriChat and receive messages from millions of mobile users worldwide. One-time $99 blue badge verification for permanent trust. OTP delivery available globally.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="#verification"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Get Blue Badge <Award className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-900/60 p-2 backdrop-blur">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/Zurichat_header.png"
                  alt="ZuriChat business messaging interface showing unified inbox and customer conversations"
                  className="rounded-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Why businesses choose ZuriChat</h2>
            <p className="section-subtitle mx-auto mt-4">Connect with customers on the leading mobile platform with enterprise-grade features.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Business benefits that matter</h2>
            <p className="section-subtitle mx-auto mt-4">Advantages that set ZuriChat Business apart from other messaging platforms.</p>
          </div>
          <div className="grid gap-8 md:grid-cols-2">
            {benefits.map((b) => (
              <div key={b.title} className="flex gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-100 text-brand-600">
                  <Check className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">{b.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{b.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="verification" className="py-24" aria-labelledby="verification-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="verification-heading" className="section-title">Simple 4-step verification process</h2>
            <p className="section-subtitle mx-auto mt-4">Get your blue badge in 4 simple steps. One-time $99 fee for permanent verification.</p>
          </div>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {verificationSteps.map((s) => (
              <div key={s.step} className="relative">
                <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand-600 text-2xl font-bold text-white">
                  {s.step}
                </div>
                <h3 className="text-lg font-bold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="industries-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="industries-heading" className="section-title">Perfect for every African industry</h2>
            <p className="section-subtitle mx-auto mt-4">From retail shops to professional services, ZuriChat Business works for all African businesses.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {industries.map((i) => (
              <div key={i.title} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
                  <Building2 className="h-6 w-6" />
                </div>
                <h3 className="text-base font-bold text-slate-900">{i.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{i.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about ZuriChat Business verification and messaging.</p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.question} className="rounded-2xl border border-slate-200 bg-white">
                <button
                  onClick={() => setOpenFaq(openFaq === faq.question ? null : faq.question)}
                  className="flex w-full items-center justify-between p-6 text-left"
                >
                  <span className="text-base font-semibold text-slate-900">{faq.question}</span>
                  {openFaq === faq.question ? (
                    <ChevronUp className="h-5 w-5 text-slate-500" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-slate-500" />
                  )}
                </button>
                {openFaq === faq.question && (
                  <div className="px-6 pb-6">
                    <p className="text-sm text-slate-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-950 py-24 text-white">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-extrabold tracking-tight sm:text-4xl">
            Ready to connect with millions of African mobile users?
          </h2>
          <p className="mt-4 text-lg text-slate-300">
            Start your free trial today and get your blue badge verification. One-time $99 fee for permanent business verification.
          </p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:justify-center">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100"
            >
              Get Started Free <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}